import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Globe, Highlighter, Users } from 'lucide-react';

import { AppShell } from '@/components/AppShell';
import { HighlightFeed } from '@/components/HighlightFeed';
import { OpenAnything } from '@/components/OpenAnything';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { HighlightsFeedMode } from '@/hooks/useHighlights';

const Index = () => {
  const { user } = useCurrentUser();
  const [tab, setTab] = useState<HighlightsFeedMode>('global');

  useSeoMeta({
    title: 'glowmark — read the best bits',
    description:
      'A reading client for Nostr highlights: the passages from articles, notes, and the open web that real people stopped to mark.',
  });

  return (
    <AppShell>
      {/* Hero — shown to logged-out visitors */}
      {!user && (
        <section className="relative isolate overflow-hidden border-b border-border/70">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 bg-[radial-gradient(60%_80%_at_50%_0%,hsl(45_95%_65%/0.25),transparent_70%)] dark:bg-[radial-gradient(60%_80%_at_50%_0%,hsl(45_95%_45%/0.12),transparent_70%)]"
          />
          <div className="container max-w-3xl py-16 sm:py-20 text-center">
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight font-medium tracking-tight">
              Read the <mark className="hl-mark px-1">best bits</mark>.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              glowmark collects the passages people highlight across Nostr — excerpts from
              articles, notes, and the open web that real readers stopped to mark.
            </p>
          </div>
        </section>
      )}

      <div className="container max-w-2xl py-8">
        <OpenAnything className="mb-6" />

        <Tabs value={tab} onValueChange={(v) => setTab(v as HighlightsFeedMode)}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="global">
              <Globe />
              Global
            </TabsTrigger>
            <TabsTrigger value="following">
              <Users />
              Following
            </TabsTrigger>
            <TabsTrigger value="mine">
              <Highlighter />
              Mine
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="global" className="mt-0">
              {tab === 'global' && <HighlightFeed mode="global" />}
            </TabsContent>
            <TabsContent value="following" className="mt-0">
              {tab === 'following' && <HighlightFeed mode="following" />}
            </TabsContent>
            <TabsContent value="mine" className="mt-0">
              {tab === 'mine' && <HighlightFeed mode="mine" />}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </AppShell>
  );
};

export default Index;
